import { useCallback, useMemo } from "react";
import { Badge, Card, CardContent, CardHeader, Stack, Typography } from "@altimateai/lego";
import { DataTable } from "@/components/tables";
import {
  FilterType,
  createAPIDropdownFilter,
  type TableHeaderType,
} from "@/components/tables/types";
import { Paginate } from "@/components/paginate";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";
import { FilterApplicableFor } from "@/components/tags/types";
import { formatNumber } from "@/uiCore/components/formatters/format";
import { getAimlEndpointNames } from "@/helpers/apis";
import { useAimlEndpointFilters } from "../../hooks";
import { UnifiedAIMLEndpointItem } from "../types";
import { getEndpointTypeBadge, getServingTypeLabel } from "../utils";

interface Props {
  endpoints: UnifiedAIMLEndpointItem[];
  totalCount: number;
  isLoading: boolean;
  isError: boolean;
  entityTypeFilter: string;
  formatCurrency: (value: number) => string;
  startDate: string;
  endDate: string;
  workspaceId?: string;
}

const SORT_PARAM = "sort_by";
const SORT_ORDER_PARAM = "sort_order";
const FILTER_PORTAL_ID = "aiml-endpoints-filter";
const SAVED_FILTERS_PORTAL_ID = "aiml-endpoints-saved-filters";

const SERVICE_URL_SEGMENT: Record<UnifiedAIMLEndpointItem["service"], string> = {
  model_serving: "model-serving",
  vector_search: "vector-search",
  ai_gateway: "ai-gateway",
};

export const EndpointsTable = ({
  endpoints,
  totalCount,
  isLoading,
  isError,
  entityTypeFilter,
  formatCurrency,
  startDate,
  endDate,
  workspaceId,
}: Props) => {
  // Pagination state lives in URL — `useTablePagination` reads it from the
  // shared DatatableContext (DataTable registers `page`/`pageSize` on mount)
  // and writes back to URL on user navigation.
  const { page, pageSize, setPage, setPageSize } = useTablePagination();

  // Filter-option values come from the backend filter-values endpoint scoped to
  // the date window — the dropdown can only offer entity types that actually
  // appear on rows in [startDate, endDate].
  const { data: filterOptions } = useAimlEndpointFilters({
    start_date: startDate,
    end_date: endDate,
  });

  const entityTypeOptions = useMemo(
    () =>
      (filterOptions?.entity_types ?? []).map((t: string) => ({
        label: getServingTypeLabel(t),
        value: t,
      })),
    [filterOptions?.entity_types]
  );

  const columns: TableHeaderType<UnifiedAIMLEndpointItem>[] = useMemo(
    () => [
      {
        id: 1,
        uid: "endpoint_name",
        label: "Endpoint Name",
        width: "40%",
        sort: {
          sortSearchKey: SORT_PARAM,
          sortOrderSearchKey: SORT_ORDER_PARAM,
          sortParamValue: "endpoint_name",
        },
        // Server-searched dropdown: the full name set is too large to ship at
        // once, so options are fetched per keystroke (substring `search`) and
        // paged. The selected name(s) are sent back as the exact endpoint_name
        // filter on the list query.
        filter: createAPIDropdownFilter({
          filterType: FilterType.API_DROPDOWN,
          searchKey: "endpoint_name",
          label: "Endpoint Name",
          apiFunction: ({ prefix, page }) =>
            getAimlEndpointNames({
              start_date: startDate,
              end_date: endDate,
              search: prefix || undefined,
              page,
              size: 20,
              workspace_id: workspaceId,
            }),
          extractorFunction: data => data?.items?.map(name => ({ label: name, value: name })) ?? [],
          seeMore: true,
        }),
        render: (item: UnifiedAIMLEndpointItem) => (
          <Typography weight="medium" className="al-text-brand">
            {item.endpoint_name}
          </Typography>
        ),
      },
      {
        id: 2,
        uid: "entity_type",
        label: "Type",
        width: "25%",
        sort: {
          sortSearchKey: SORT_PARAM,
          sortOrderSearchKey: SORT_ORDER_PARAM,
          sortParamValue: "entity_type",
        },
        filter: {
          filterType: FilterType.DROPDOWN_SINGLE,
          searchKey: "entity_type",
          label: "Type",
          options: entityTypeOptions,
          value: entityTypeFilter,
        },
        render: (item: UnifiedAIMLEndpointItem) => {
          const { label, className } = getEndpointTypeBadge(item);
          return (
            <Badge interactive={false} className={className}>
              {label}
            </Badge>
          );
        },
      },
      {
        id: 3,
        uid: "total_cost_usd",
        label: "Total Cost",
        width: "17.5%",
        sort: {
          sortSearchKey: SORT_PARAM,
          sortOrderSearchKey: SORT_ORDER_PARAM,
          sortParamValue: "total_cost_usd",
        },
        render: (item: UnifiedAIMLEndpointItem) => (
          <Typography weight="medium">{formatCurrency(item.total_cost_usd)}</Typography>
        ),
      },
      {
        id: 4,
        uid: "total_dbus",
        label: "DBUs",
        width: "17.5%",
        sort: {
          sortSearchKey: SORT_PARAM,
          sortOrderSearchKey: SORT_ORDER_PARAM,
          sortParamValue: "total_dbus",
        },
        render: (item: UnifiedAIMLEndpointItem) => (
          <Typography weight="medium">{formatNumber(item.total_dbus)}</Typography>
        ),
      },
    ],
    [formatCurrency, entityTypeFilter, entityTypeOptions, startDate, endDate, workspaceId]
  );

  const getRowUrl = useCallback(
    (item: UnifiedAIMLEndpointItem) => {
      const segment = SERVICE_URL_SEGMENT[item.service];
      if (!segment) return "";
      return `/ai-ml/${segment}/${encodeURIComponent(item.endpoint_name)}?start_date=${startDate}&end_date=${endDate}`;
    },
    [startDate, endDate]
  );

  const numPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <Card>
      <CardHeader>
        <Stack direction="row" align="center" justify="between" className="al-gap-2 al-flex-wrap">
          <Typography variant="section-heading">Endpoints</Typography>
          <Stack direction="row" gap="sm" className="al-flex-wrap">
            <div id={SAVED_FILTERS_PORTAL_ID} />
            <div id={FILTER_PORTAL_ID} />
          </Stack>
        </Stack>
      </CardHeader>
      <CardContent>
        {isError ? (
          <div className="al-p-6 al-text-center">
            <Typography className="al-text-red-500">
              Failed to load endpoints. Please try again.
            </Typography>
          </div>
        ) : (
          <Stack direction="column" gap="md">
            <DataTable
              header={columns}
              data={{ items: endpoints }}
              isLoading={isLoading}
              getRowUrl={getRowUrl}
              defaultSort={{ sortParamValue: "total_cost_usd", order: "desc" }}
              applicableFor={FilterApplicableFor.AI_ML_ENDPOINTS}
              portals={{ filter: FILTER_PORTAL_ID, savedFilters: SAVED_FILTERS_PORTAL_ID }}
              disableDynamicHeight
            />
            <Paginate
              itemCount={totalCount}
              page={page}
              pageSize={pageSize}
              numPages={numPages}
              onPageClick={setPage}
              onPageSizeChange={setPageSize}
              showPageSize
              isLoading={isLoading}
            />
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};
